/**
 * Retail > Fleet Map (Project Possible)
 *
 * /retail/fleet-map — a faithful React/react-leaflet rebuild of Franco's
 * original self-contained Leaflet scorecard map
 * (Ampersand_Fleet_Landlord_Scorecard_Map). Layout: a 420px left column
 * (brand header, KPI boxes, filters, quick-filter chips, visible-KPI boxes,
 * legend with counts) beside a main area holding a CartoDB Positron map of all
 * L&P stores over a full-width responsive store-card grid. Markers are donut
 * markers — filled by landlord group, ringed by YTD comp severity, with a gold
 * halo for the Pilot 16. Each marker carries a rich popup (sales scorecard,
 * avg $/TRX, pilot memo). The map is locked to the continental US: it fits the
 * US bbox on mount, pan is hard-clamped to it, and minZoom is the US-fill zoom
 * so the user can zoom in but not out past the US. Clicking a store card OR a
 * marker opens a centered, scrollable detail modal (StoreDetailModal); neither
 * path touches Leaflet projection, so there is no fly-to / openPopup crash.
 * Markers keep a lightweight hover tooltip for the quick one-liner.
 *
 * RBAC: an Everyone tool, gated only on the core:read session baseline (every
 * signed-in employee). Access-denied card for anyone who somehow lacks it.
 *
 * Resilience: the page is wrapped in an ErrorBoundary, every metrics read is
 * defensive (sales/avg_trx can be null on the list endpoint), and all hooks
 * use retry:false with stable empty defaults so the page renders a clean "no
 * data yet" state on fetch failure instead of crashing.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { latLngBounds } from 'leaflet';
import { Download, Loader2, X } from 'lucide-react';
import { InfoTooltip } from '@ampersandbrands/ui';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Pill } from '@/components/Pill';
import { FilterLabel } from '@/components/FilterLabel';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { compColorClass } from '@/lib/compColor';
import { FOUR_FIVE_FOUR_CALENDAR_ARTICLE_URL, FOUR_FIVE_FOUR_CALENDAR_TOOLTIP } from '@/lib/helpLinks';
import { useSession } from '@/lib/useSession';
import {
  useRetailWeeks,
  useRetailStores,
  useRetailStoreDetail,
  downloadRetailXlsx,
  severityColor,
  severityLabel,
  landlordColor,
  computeFleetStats,
  markerRadius,
  maxYtdSales,
  hasRetailScorecard,
  isPilot,
  formatComp,
  formatDollars,
  formatDpt,
  formatWeekDate,
  formatComputedAt,
  splitMonthlyByFy,
  formatFiscalMonth,
  PILOT_GOLD,
  SEVERITY_ORDER,
  type RetailStoreRow,
  type RetailSalesWindow,
  type RetailMonthlyRow,
  type SeverityTier,
} from '@/lib/useRetailStores';

// ---------------------------------------------------------------------------
// Filter + sort vocabulary
// ---------------------------------------------------------------------------

const ALL = 'All';

type CompBasis = 'All' | 'true_comp' | 'not_true_comp' | 'has_baseline' | 'no_baseline';

const COMP_BASIS_OPTIONS: { value: CompBasis; label: string }[] = [
  { value: 'All', label: 'All stores' },
  { value: 'true_comp', label: 'Scorecard true-comp flag' },
  { value: 'not_true_comp', label: 'Not true-comp flag' },
  { value: 'has_baseline', label: 'Has FY25 baseline' },
  { value: 'no_baseline', label: 'New / no FY25' },
];

type PilotFilter = 'All' | 'pilot' | 'non_pilot';

const PILOT_OPTIONS: { value: PilotFilter; label: string }[] = [
  { value: 'All', label: 'All stores' },
  { value: 'pilot', label: 'Pilot 16 only' },
  { value: 'non_pilot', label: 'Non-pilot only' },
];

type SortKey = 'pilotRank' | 'ytdCompWorst' | 'ytdSalesHigh' | 'landlord' | 'storeNo';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'pilotRank', label: 'Pilot memo rank first' },
  { value: 'ytdCompWorst', label: 'YTD comp - worst first' },
  { value: 'ytdSalesHigh', label: 'YTD FY26 sales - high first' },
  { value: 'landlord', label: 'Landlord group' },
  { value: 'storeNo', label: 'Store #' },
];

const CRITICAL_SEVERITY: SeverityTier = 'critical';

type Filters = {
  search: string;
  landlordGroup: string;
  landlordDetail: string;
  region: string;
  district: string;
  severity: string;
  salesTier: string;
  pilot: PilotFilter;
  compBasis: CompBasis;
  sort: SortKey;
};

const EMPTY_FILTERS: Filters = {
  search: '',
  landlordGroup: ALL,
  landlordDetail: ALL,
  region: ALL,
  district: ALL,
  severity: ALL,
  salesTier: ALL,
  pilot: ALL,
  compBasis: 'All',
  sort: 'pilotRank',
};

function salesTierLabel(t: string): string {
  switch (t) {
    case '200k+':      return '$200k+';
    case '150k-200k':  return '$150k-200k';
    case '100k-150k':  return '$100k-150k';
    case 'under_100k': return 'Under $100k';
    default:           return t;
  }
}

// Distinct, sorted, non-empty values for a string getter over the fleet.
function uniqueSorted(
  stores: RetailStoreRow[],
  getter: (s: RetailStoreRow) => string | null,
): string[] {
  const set = new Set<string>();
  for (const s of stores) {
    const v = getter(s);
    if (v != null && String(v).trim() !== '') set.add(v);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

function n(v: number | null | undefined, def: number): number {
  return v == null || Number.isNaN(v) ? def : v;
}

function sortStores(stores: RetailStoreRow[], sort: SortKey): RetailStoreRow[] {
  const cp = [...stores];
  switch (sort) {
    case 'ytdSalesHigh':
      cp.sort((a, b) => n(b.sales?.ytd_ty, -Infinity) - n(a.sales?.ytd_ty, -Infinity));
      break;
    case 'pilotRank':
      cp.sort(
        (a, b) =>
          n(a.pilot_rank, 9999) - n(b.pilot_rank, 9999) ||
          n(Number(a.store_no), 99999) - n(Number(b.store_no), 99999),
      );
      break;
    case 'landlord':
      cp.sort(
        (a, b) =>
          String(a.landlord_group ?? '').localeCompare(String(b.landlord_group ?? '')) ||
          a.store_name.localeCompare(b.store_name),
      );
      break;
    case 'storeNo':
      cp.sort((a, b) => n(Number(a.store_no), 99999) - n(Number(b.store_no), 99999));
      break;
    case 'ytdCompWorst':
    default:
      cp.sort((a, b) => n(a.sales?.ytd_comp, Infinity) - n(b.sales?.ytd_comp, Infinity));
      break;
  }
  return cp;
}

// ---------------------------------------------------------------------------
// Presentational atoms
// ---------------------------------------------------------------------------

function StatBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="text-lg font-bold leading-tight text-amp-ink dark:text-amp-dark-ink">{value}</div>
      <div className="mt-0.5 text-[10px] leading-tight text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</div>
    </div>
  );
}

function VisibleStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded border border-amp-line bg-amp-cream/60 p-1.5 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm/60">
      <div className="text-sm font-bold text-amp-ink dark:text-amp-dark-ink">{value}</div>
      <div className="text-[9px] text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <FilterLabel>{label}</FilterLabel>
      {children}
    </div>
  );
}

// SELECT_CLS retained for the search input and week/sort selects that stay
// as plain <select> elements (they are functional controls, not filter pickers).
const SELECT_CLS =
  'w-full border border-amp-line rounded px-2 py-1.5 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';

// FleetSelect wraps SearchableSelect for single-value string filters.
// The filter state stores a plain string; SearchableSelect expects T[].
// We bridge them: selected=[value], onChange extracts next[0]??ALL.
function FleetSelect({
  value,
  onChange,
  allLabel,
  options,
  ariaLabel,
  optionLabels,
}: {
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: string[];
  ariaLabel: string;
  /** Optional map from raw option value to display label. */
  optionLabels?: (o: string) => string;
}) {
  const selectOptions = useMemo(
    () =>
      [{ value: ALL, label: allLabel }, ...options.map((o) => ({ value: o, label: optionLabels ? optionLabels(o) : o }))],
    [options, allLabel, optionLabels],
  );
  return (
    <SearchableSelect
      ariaLabel={ariaLabel}
      options={selectOptions}
      selected={[value]}
      onChange={(next) => onChange(next[0] ?? ALL)}
      multiSelect={false}
      placeholder={allLabel}
      className="w-full"
    />
  );
}

// FleetSelectFixed wraps SearchableSelect for filters with a fixed, pre-built
// option list (severity, pilot, comp basis, etc.) that already carry labels.
function FleetSelectFixed<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel: string;
}) {
  return (
    <SearchableSelect
      ariaLabel={ariaLabel}
      options={options}
      selected={[value]}
      onChange={(next) => onChange((next[0] ?? options[0]?.value ?? value) as T)}
      multiSelect={false}
      placeholder={options[0]?.label ?? 'All'}
      className="w-full"
    />
  );
}

function GroupChip({ group }: { group: string | null }) {
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
      style={{ background: landlordColor(group) }}
    >
      {group ?? 'Unknown'}
    </span>
  );
}

function LightChip({ children }: { children: React.ReactNode }) {
  return <Pill variant="outline">{children}</Pill>;
}

function dash(v: string | number | null | undefined): string {
  if (v == null || v === '') return '--';
  return String(v);
}

function boolText(v: boolean | null | undefined): string {
  if (v == null) return '--';
  return v ? 'Yes' : 'No';
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function AccessDeniedCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
        Store Fleet Map is gated by the core:read capability.
      </p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Ask IT to grant you access.</p>
    </div>
  );
}

function NoDataCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">No store data yet.</p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        The retail metrics refresh runs daily. Check back after the first run completes.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Popup body (replicates Franco's popupHTML)
// ---------------------------------------------------------------------------

type PeriodRow = { label: string; key: 'l7' | 'l4' | 'l13' | 'ytd' };

const PERIODS: PeriodRow[] = [
  { label: 'L7', key: 'l7' },
  { label: 'L4', key: 'l4' },
  { label: 'L13', key: 'l13' },
  { label: 'YTD', key: 'ytd' },
];

function MetricTable({ window, money }: { window: RetailSalesWindow | null; money: boolean }) {
  const fmt = money ? formatDollars : formatDpt;
  return (
    <table className="w-full border-collapse text-[11px]">
      <thead>
        <tr className="text-left text-amp-ink-muted dark:text-amp-dark-ink-muted">
          <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">Period</th>
          <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">FY26</th>
          <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">FY25</th>
          <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">Comp</th>
        </tr>
      </thead>
      <tbody>
        {PERIODS.map((p) => {
          const ty = window?.[`${p.key}_ty`] ?? null;
          const ly = window?.[`${p.key}_ly`] ?? null;
          const comp = window?.[`${p.key}_comp`] ?? null;
          return (
            <tr key={p.key}>
              <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">{p.label}</td>
              <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{fmt(ty)}</td>
              <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{fmt(ly)}</td>
              <td className={`py-0.5 ${compColorClass(comp)}`}>{formatComp(comp)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 text-[11px] font-bold text-amp-ink-muted dark:text-amp-dark-ink-muted">
      {children}
    </div>
  );
}

// The popup reads everything off the store row from the list endpoint (which
// carries sales/avg_trx for the selected week) plus the optional memo fields.
// This matches Franco's marker-popup-is-everything design and avoids a second
// fetch (and the panel-crash class of bug) entirely.
function StorePopupBody({ store }: { store: RetailStoreRow }) {
  const pilot = isPilot(store);
  return (
    <div className="min-w-[300px] max-w-[420px]" style={{ fontFamily: 'inherit' }}>
      <div className="mb-1.5 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {dash(store.city)}, {dash(store.state)}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-1">
        <GroupChip group={store.landlord_group} />
        <LightChip>{severityLabel(store.severity)}</LightChip>
        <LightChip>YTD {formatComp(store.sales?.ytd_comp)}</LightChip>
      </div>

      <table className="mb-1 w-full border-collapse text-[11px]">
        <tbody>
          <tr>
            <td className="w-1/3 py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Landlord detail</td>
            <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{dash(store.landlord_detail)}</td>
          </tr>
          <tr>
            <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Region / district</td>
            <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{dash(store.region)} / {dash(store.district)}</td>
          </tr>
          <tr>
            <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Alpha</td>
            <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{dash(store.alpha)}</td>
          </tr>
          <tr>
            <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Scorecard true comp</td>
            <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{boolText(store.scorecard_true_comp)}</td>
          </tr>
          <tr>
            <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">FY25 baseline</td>
            <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{boolText(store.has_fy25_baseline)}</td>
          </tr>
        </tbody>
      </table>

      <SectionTitle>Sales scorecard</SectionTitle>
      <MetricTable window={store.sales} money />

      <SectionTitle>Average dollars per transaction</SectionTitle>
      <MetricTable window={store.avg_trx} money={false} />

      {pilot && (
        <>
          <SectionTitle>Pilot 16 memo</SectionTitle>
          <table className="w-full border-collapse text-[11px]">
            <tbody>
              <tr>
                <td className="w-1/2 py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Pilot rank</td>
                <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">#{dash(store.pilot_rank)}</td>
              </tr>
              <tr>
                <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Memo district / alpha</td>
                <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{dash(store.district)} / {dash(store.alpha)}</td>
              </tr>
              <tr>
                <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Memo 2025 sales</td>
                <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{formatDollars(store.memo_2025_sales)}</td>
              </tr>
              <tr>
                <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Memo % of total</td>
                <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">{formatComp(store.memo_pct_of_total)}</td>
              </tr>
              <tr>
                <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Memo YTD comp</td>
                <td className={`py-0.5 ${compColorClass(store.memo_ytd_comp)}`}>{formatComp(store.memo_ytd_comp)}</td>
              </tr>
              <tr>
                <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">Current YTD comp</td>
                <td className={`py-0.5 ${compColorClass(store.sales?.ytd_comp)}`}>{formatComp(store.sales?.ytd_comp)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {store.landlord_source_note && (
        <div className="mt-2 text-[10px] leading-snug text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {store.landlord_source_note}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Monthly performance section (calls detail endpoint; appended below StorePopupBody)
// ---------------------------------------------------------------------------

/**
 * Renders a compact "Monthly performance" table for the current fiscal year
 * using data from GET /v1/retail/stores/{store_no}. Shows Month, FY26 sales,
 * Comp %, and vs Plan columns. Negative comps and negative vs-plan render in
 * red; null plan/vs_plan shows a muted dash. While loading, shows an inline
 * spinner; on error, shows a muted "monthly data unavailable" line without
 * crashing. Prior-FY rows are omitted (current FY is the requirement).
 */
function MonthlyPerformanceSection({ storeNo }: { storeNo: string }) {
  const { monthly, isLoading, error } = useRetailStoreDetail(storeNo);

  if (isLoading) {
    return (
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Loading monthly data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Monthly data unavailable.
      </div>
    );
  }

  const { currentFy } = splitMonthlyByFy(monthly);

  if (currentFy.length === 0) {
    return (
      <div className="mt-3 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        No monthly data for the current fiscal year.
      </div>
    );
  }

  // Sort ascending by fiscal_month_start so months render chronologically.
  const sorted = [...currentFy].sort((a, b) =>
    a.fiscal_month_start.localeCompare(b.fiscal_month_start),
  );
  const siblings = sorted.map((r) => r.fiscal_month_start);

  return (
    <>
      <SectionTitle>Monthly performance</SectionTitle>
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr className="text-left text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">Month</th>
            <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">FY26</th>
            <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">Comp %</th>
            <th className="border-b border-amp-line py-0.5 font-medium dark:border-amp-dark-line">vs Plan</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row: RetailMonthlyRow) => (
            <tr key={row.fiscal_month_start}>
              <td className="py-0.5 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {formatFiscalMonth(row.fiscal_month_start, siblings)}
              </td>
              <td className="py-0.5 text-amp-ink dark:text-amp-dark-ink">
                {formatDollars(row.sales_ty)}
              </td>
              <td className={`py-0.5 ${compColorClass(row.comp)}`}>
                {formatComp(row.comp)}
              </td>
              <td className={`py-0.5 ${row.vs_plan == null ? 'text-amp-ink-muted dark:text-amp-dark-ink-muted' : compColorClass(row.vs_plan)}`}>
                {row.vs_plan == null ? <span aria-label="no plan">--</span> : formatComp(row.vs_plan)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// ---------------------------------------------------------------------------
// Map helpers
// ---------------------------------------------------------------------------

// Continental US bounding box (SW -> NE). Used to lock the map: the view fits
// this box on mount, panning is hard-clamped inside it (maxBounds +
// viscosity), and the minimum zoom is derived from the zoom at which this box
// fills the viewport, so the user cannot zoom out past the US. Zooming IN is
// free up to MAX_ZOOM.
const US_BOUNDS = latLngBounds([24.4, -125.0], [49.4, -66.9]);
const MAX_ZOOM = 16;

// Locks the map to the continental US: fit on mount, clamp pan to the US box,
// and set minZoom to the US-fill zoom so the user can zoom in but not out past
// the US. Recomputes the fit/minZoom on container resize so the clamp stays
// correct if the layout reflows.
function LockToUs() {
  const map = useMap();
  useEffect(() => {
    const applyLock = () => {
      map.setMaxBounds(US_BOUNDS);
      // getBoundsZoom(bounds, inside=false) returns the largest zoom at which
      // the bounds still fit INSIDE the viewport, so the whole continental US
      // stays visible (with a little margin) rather than cropping the edges.
      const fitZoom = map.getBoundsZoom(US_BOUNDS, false);
      const usFloor = Math.max(2, Math.floor(fitZoom));
      map.setMinZoom(usFloor);
      map.setMaxZoom(MAX_ZOOM);
      map.fitBounds(US_BOUNDS);
    };
    applyLock();
    map.on('resize', applyLock);
    return () => {
      map.off('resize', applyLock);
    };
  }, [map]);
  return null;
}

// ---------------------------------------------------------------------------
// Store detail modal (centered overlay, scrollable body)
// ---------------------------------------------------------------------------

// Centered detail modal mirroring the SignOutModal pattern in shell/Header.tsx:
// fixed inset-0, z-50, semi-transparent backdrop, backdrop-click + Escape to
// close, amp color tokens, dark-mode aware. The body is scrollable so the full
// scorecard (FY25 baseline line, sales table, avg $/TRX, pilot memo, source
// note) is never clipped. Reuses StorePopupBody for the detail content. This
// path never touches Leaflet projection, so it cannot trigger the fly-to /
// openPopup null-deref crash the old SelectionFlyer caused.
function StoreDetailModal({ store, onClose }: { store: RetailStoreRow; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pilot = isPilot(store);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="store-detail-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[600px] flex-col overflow-hidden rounded-lg border border-amp-line bg-amp-white shadow-xl dark:border-amp-dark-line dark:bg-amp-dark-surface"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: store name + pilot star/rank, close button */}
        <div className="flex items-start justify-between gap-3 border-b border-amp-line px-4 py-3 dark:border-amp-dark-line">
          <h2
            id="store-detail-title"
            className="text-base font-bold text-amp-ink dark:text-amp-dark-ink"
          >
            {store.store_no} &middot; {store.store_name}
            {pilot && store.pilot_rank != null && (
              <span style={{ color: PILOT_GOLD }}> &#9733; Pilot #{store.pilot_rank}</span>
            )}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close store detail"
            className="flex-shrink-0 rounded-md p-1 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        {/* Scrollable detail body */}
        <div className="overflow-y-auto px-4 py-3">
          <ErrorBoundary>
            <StorePopupBody store={store} />
            <MonthlyPerformanceSection storeNo={store.store_no} />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Store list card (replicates Franco's .store-card)
// ---------------------------------------------------------------------------

function StoreCard({ store, onSelect }: { store: RetailStoreRow; onSelect: (store: RetailStoreRow) => void }) {
  const pilot = isPilot(store);
  const ring = severityColor(store.severity);
  return (
    <button
      type="button"
      onClick={() => onSelect(store)}
      style={pilot ? { boxShadow: `inset 3px 0 0 ${PILOT_GOLD}` } : undefined}
      className={`h-full w-full rounded-lg border bg-amp-white p-2.5 text-left transition hover:-translate-y-px hover:shadow-md dark:bg-amp-dark-surface ${
        pilot ? 'border-[#D4AF37]' : 'border-amp-line dark:border-amp-dark-line'
      }`}
    >
      <div className="text-[13px] font-bold leading-tight text-amp-ink dark:text-amp-dark-ink">
        {store.store_no} &middot; {store.store_name}
        {pilot && store.pilot_rank != null && (
          <span style={{ color: PILOT_GOLD }}> &#9733; #{store.pilot_rank}</span>
        )}{' '}
        <span style={{ color: ring }} className="font-semibold">
          {formatComp(store.sales?.ytd_comp)}
        </span>
      </div>
      <div className="mt-0.5 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {dash(store.center)} &middot; {dash(store.city)}, {dash(store.state)}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <GroupChip group={store.landlord_group} />
        <LightChip>{severityLabel(store.severity)}</LightChip>
        <LightChip>{formatDollars(store.sales?.ytd_ty)} YTD26</LightChip>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function RetailFleetMapInner() {
  const { user } = useSession();
  const groups = user?.groups ?? [];
  const canView = hasRetailScorecard(groups);

  const { weeks, isLoading: weeksLoading } = useRetailWeeks();
  const [selectedWeek, setSelectedWeek] = useState<string>('');

  useEffect(() => {
    const first = weeks[0];
    if (first !== undefined && !selectedWeek) {
      setSelectedWeek(first);
    }
  }, [weeks, selectedWeek]);

  const { stores, weekEnding, computedAt, isLoading: storesLoading, error: storesError } =
    useRetailStores({ week_ending: selectedWeek || undefined });

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  function setFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Selecting a store (via a card click or a marker click) opens the centered
  // detail modal. Holds the full store row so the modal header and body render
  // without any Leaflet interaction.
  const [selectedStore, setSelectedStore] = useState<RetailStoreRow | null>(null);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadRetailXlsx(weekEnding);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  // Clicking a store card or a marker opens the detail modal for that store.
  function openStore(store: RetailStoreRow) {
    setSelectedStore(store);
  }

  // --- filter option lists (from the loaded fleet) --------------------------
  const landlordGroups = useMemo(() => uniqueSorted(stores, (s) => s.landlord_group), [stores]);
  const landlordDetails = useMemo(() => uniqueSorted(stores, (s) => s.landlord_detail), [stores]);
  const regions = useMemo(() => uniqueSorted(stores, (s) => s.region), [stores]);
  const districts = useMemo(() => uniqueSorted(stores, (s) => s.district), [stores]);
  const salesTiers = useMemo(() => uniqueSorted(stores, (s) => s.sales_tier), [stores]);
  const severities = useMemo(
    () => SEVERITY_ORDER.filter((tier) => stores.some((s) => s.severity === tier)),
    [stores],
  );

  // --- apply filters --------------------------------------------------------
  const filteredStores = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const list = stores.filter((s) => {
      const hay = [
        s.store_no, s.store_name, s.center, s.city, s.state, s.region,
        s.landlord_group, s.landlord_detail, s.district, s.alpha, s.sales_tier,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      let basisOk = true;
      if (filters.compBasis === 'true_comp') basisOk = s.scorecard_true_comp === true;
      else if (filters.compBasis === 'not_true_comp') basisOk = s.scorecard_true_comp !== true;
      else if (filters.compBasis === 'has_baseline') basisOk = s.has_fy25_baseline === true || s.severity !== 'no_baseline';
      else if (filters.compBasis === 'no_baseline') basisOk = s.has_fy25_baseline === false || s.severity === 'no_baseline';

      const pilotOk =
        filters.pilot === 'All' || (filters.pilot === 'pilot' ? isPilot(s) : !isPilot(s));

      return (
        (!q || hay.includes(q)) &&
        (filters.landlordGroup === ALL || s.landlord_group === filters.landlordGroup) &&
        (filters.landlordDetail === ALL || s.landlord_detail === filters.landlordDetail) &&
        (filters.region === ALL || s.region === filters.region) &&
        (filters.district === ALL || s.district === filters.district) &&
        (filters.severity === ALL || s.severity === filters.severity) &&
        (filters.salesTier === ALL || s.sales_tier === filters.salesTier) &&
        pilotOk &&
        basisOk
      );
    });
    return sortStores(list, filters.sort);
  }, [stores, filters]);

  // --- stat-box math --------------------------------------------------------
  const fleetStats = useMemo(() => computeFleetStats(stores), [stores]);
  const visibleStats = useMemo(() => computeFleetStats(filteredStores), [filteredStores]);
  const maxYtd = useMemo(() => maxYtdSales(stores), [stores]);

  // Legend counts over the full loaded fleet.
  const landlordCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of stores) {
      const g = s.landlord_group ?? 'Unknown';
      m.set(g, (m.get(g) ?? 0) + 1);
    }
    return m;
  }, [stores]);

  const severityCounts = useMemo(() => {
    const m = new Map<SeverityTier, number>();
    for (const s of stores) m.set(s.severity, (m.get(s.severity) ?? 0) + 1);
    return m;
  }, [stores]);

  // --- quick filters --------------------------------------------------------
  const reset = () => setFilters(EMPTY_FILTERS);
  // Each quick-filter toggles: a second click on an already-active chip clears
  // only that filter back to its default, leaving all other active filters intact.
  const quickPilot = () => {
    if (filters.pilot === 'pilot') {
      setFilters((f) => ({ ...f, pilot: EMPTY_FILTERS.pilot, sort: EMPTY_FILTERS.sort }));
    } else {
      setFilters({ ...EMPTY_FILTERS, pilot: 'pilot', sort: 'pilotRank' });
    }
  };
  const quickCritical = () =>
    setFilter('severity', filters.severity === CRITICAL_SEVERITY ? ALL : CRITICAL_SEVERITY);
  const quickBrookfield = () =>
    setFilter('landlordGroup', filters.landlordGroup === 'Brookfield' ? ALL : 'Brookfield');
  const quickTrueComp = () =>
    setFilter('compBasis', filters.compBasis === 'true_comp' ? 'All' : 'true_comp');

  const isFilterActive =
    filters.search !== '' ||
    filters.landlordGroup !== ALL ||
    filters.landlordDetail !== ALL ||
    filters.region !== ALL ||
    filters.district !== ALL ||
    filters.severity !== ALL ||
    filters.salesTier !== ALL ||
    filters.pilot !== 'All' ||
    filters.compBasis !== 'All' ||
    filters.sort !== EMPTY_FILTERS.sort;

  const isLoading = weeksLoading || storesLoading;
  const hasStores = stores.length > 0;

  if (!user && !isLoading) return null; // AuthGate handles unauthenticated state

  if (user && !canView) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Fleet Map</h1>
        <AccessDeniedCard />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      {/* Header */}
      <div className="shrink-0 flex items-start justify-between gap-3">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.3em] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Ampersand Brands
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Fleet Landlord Scorecard Map
          </h1>
          <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {weekEnding ? <>Week ending {formatWeekDate(weekEnding)}</> : 'Project Possible'}
            {computedAt ? <> &middot; computed {formatComputedAt(computedAt)}</> : ''}
            {' '}&middot; landlord group fill, severity ring, gold halo = Pilot 16 memo.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting}
            title="Download fleet scorecard as Excel"
            className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Download
          </button>
          <ToolPinToggle toolSlug="fleet-map" toolName="Fleet Map" />
        </div>
      </div>

      {exportError && (
        <div className="shrink-0 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900/50 dark:bg-red-950/20">
          <p className="text-xs text-red-700 dark:text-red-400">{exportError}</p>
          <button
            type="button"
            onClick={() => setExportError(null)}
            className="ml-4 text-xs text-red-500 hover:text-red-700 dark:text-red-400"
          >
            &times;
          </button>
        </div>
      )}

      {/* Two-pane body: left controls column + main (map over card grid) */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        {/* Sidebar: stat boxes, filters, quick chips, visible KPIs, legend */}
        <aside className="w-full rounded-lg border border-amp-line bg-amp-cream/40 p-3 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm/40 md:w-[420px] md:flex-shrink-0">
          {/* Top KPI grid (full loaded fleet) */}
          <div className="mb-3 grid grid-cols-2 gap-2">
            <StatBox value={String(fleetStats.count)} label="mapped Summary stores" />
            <StatBox value={String(fleetStats.pilotCount)} label="Pilot 16 stores highlighted" />
            <StatBox value={formatComp(fleetStats.medianYtdComp)} label="median True COMP % YTD" />
            <StatBox value={formatDollars(fleetStats.ytdSales)} label="YTD FY26 total" />
          </div>

          {/* Controls */}
          <div className="mb-3 space-y-2 rounded-lg border border-amp-line bg-amp-white p-3 dark:border-amp-dark-line dark:bg-amp-dark-surface">
            <Field label="Search">
              <input
                type="text"
                className={SELECT_CLS}
                placeholder="Store, center, city, state, landlord..."
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
                aria-label="Search stores"
              />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Landlord group">
                <FleetSelect value={filters.landlordGroup} onChange={(v) => setFilter('landlordGroup', v)} allLabel="All landlord groups" options={landlordGroups} ariaLabel="Filter by landlord group" />
              </Field>
              <Field label="Landlord detail">
                <FleetSelect value={filters.landlordDetail} onChange={(v) => setFilter('landlordDetail', v)} allLabel="All landlord details" options={landlordDetails} ariaLabel="Filter by landlord detail" />
              </Field>
              <Field label="Region">
                <FleetSelect value={filters.region} onChange={(v) => setFilter('region', v)} allLabel="All regions" options={regions} ariaLabel="Filter by region" />
              </Field>
              <Field label="District">
                <FleetSelect value={filters.district} onChange={(v) => setFilter('district', v)} allLabel="All districts" options={districts} ariaLabel="Filter by district" />
              </Field>
              <Field label="YTD severity">
                <FleetSelectFixed
                  value={filters.severity}
                  onChange={(v) => setFilter('severity', v)}
                  options={[
                    { value: ALL, label: 'All severities' },
                    ...severities.map((tier) => ({ value: tier, label: severityLabel(tier) })),
                  ]}
                  ariaLabel="Filter by severity"
                />
              </Field>
              <Field label="Sales tier">
                <FleetSelect
                  value={filters.salesTier}
                  onChange={(v) => setFilter('salesTier', v)}
                  allLabel="All sales tiers"
                  options={salesTiers}
                  ariaLabel="Filter by sales tier"
                  optionLabels={salesTierLabel}
                />
              </Field>
              <Field label="Pilot 16 memo">
                <FleetSelectFixed
                  value={filters.pilot}
                  onChange={(v) => setFilter('pilot', v)}
                  options={PILOT_OPTIONS}
                  ariaLabel="Filter by pilot membership"
                />
              </Field>
              <Field label="Comp basis">
                <FleetSelectFixed
                  value={filters.compBasis}
                  onChange={(v) => setFilter('compBasis', v)}
                  options={COMP_BASIS_OPTIONS}
                  ariaLabel="Filter by comp basis"
                />
              </Field>
            </div>

            <Field label="Sort visible store list">
              <select
                className={SELECT_CLS}
                value={filters.sort}
                onChange={(e) => setFilter('sort', e.target.value as SortKey)}
                aria-label="Sort store list"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>

            {weeks.length > 0 && (
              <div>
                {/* Rendered as a div, not a <label>: the tooltip is a button, and a
                    button nested inside a label steals the label's click target. */}
                <FilterLabel as="div">
                  Week ending
                  <InfoTooltip label="the 4-5-4 calendar" learnMoreHref={FOUR_FIVE_FOUR_CALENDAR_ARTICLE_URL}>
                    {FOUR_FIVE_FOUR_CALENDAR_TOOLTIP}
                  </InfoTooltip>
                </FilterLabel>
                <select
                  className={SELECT_CLS}
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  aria-label="Select week ending"
                >
                  {weeks.map((w) => (
                    <option key={w} value={w}>{formatWeekDate(w)}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Quick-filter chips stay left-aligned in a fixed position. Reset
                appears only when a filter is active and is pushed to the right
                end (ml-auto) so the chips never shift when it shows or hides. */}
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <Pill
                variant={filters.pilot === 'pilot' ? 'selected' : 'outline'}
                aria-pressed={filters.pilot === 'pilot'}
                onClick={quickPilot}
              >
                Pilot 16
              </Pill>
              <Pill
                variant={filters.severity === CRITICAL_SEVERITY ? 'selected' : 'outline'}
                aria-pressed={filters.severity === CRITICAL_SEVERITY}
                onClick={quickCritical}
              >
                Critical
              </Pill>
              <Pill
                variant={filters.landlordGroup === 'Brookfield' ? 'selected' : 'outline'}
                aria-pressed={filters.landlordGroup === 'Brookfield'}
                onClick={quickBrookfield}
              >
                Brookfield
              </Pill>
              <Pill
                variant={filters.compBasis === 'true_comp' ? 'selected' : 'outline'}
                aria-pressed={filters.compBasis === 'true_comp'}
                onClick={quickTrueComp}
              >
                True comp
              </Pill>
              {isFilterActive && (
                <Pill variant="outline" onClick={reset} className="ml-auto">
                  Reset
                </Pill>
              )}
            </div>

            {/* Visible KPI grid (filtered set) */}
            <div className="grid grid-cols-2 gap-1.5 pt-1">
              <VisibleStat value={String(visibleStats.count)} label="visible stores" />
              <VisibleStat value={formatDollars(visibleStats.ytdSales)} label="visible YTD FY26" />
              <VisibleStat value={String(visibleStats.pilotCount)} label="visible pilot stores" />
              <VisibleStat value={formatComp(visibleStats.medianYtdComp)} label="median YTD comp" />
            </div>
          </div>

          {/* Legend */}
          {hasStores && (
            <div className="mb-3 rounded-lg border border-amp-line bg-amp-white p-3 text-[11px] dark:border-amp-dark-line dark:bg-amp-dark-surface">
              <div className="mb-1 font-bold text-amp-ink dark:text-amp-dark-ink">Landlord group</div>
              {[...landlordCounts.keys()].sort((a, b) => a.localeCompare(b)).map((g) => (
                <div key={g} className="flex items-center gap-1.5 leading-relaxed text-amp-ink dark:text-amp-dark-ink">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: landlordColor(g) }} />
                  {g}: {landlordCounts.get(g)}
                </div>
              ))}
              <div className="mb-1 mt-2 font-bold text-amp-ink dark:text-amp-dark-ink">YTD severity ring</div>
              {SEVERITY_ORDER.filter((tier) => severityCounts.has(tier)).map((tier) => (
                <div key={tier} className="flex items-center gap-1.5 leading-relaxed text-amp-ink dark:text-amp-dark-ink">
                  <span className="inline-block h-3 w-3 rounded-full bg-white" style={{ border: `3px solid ${severityColor(tier)}` }} />
                  {severityLabel(tier)}: {severityCounts.get(tier)}
                </div>
              ))}
              <div className="mt-2 flex items-center gap-1.5 leading-relaxed text-amp-ink dark:text-amp-dark-ink">
                <span className="inline-block h-3.5 w-3.5 rounded-full bg-transparent" style={{ border: `3px solid ${PILOT_GOLD}` }} />
                16-store pilot memo halo
              </div>
            </div>
          )}

          <div className="mt-4 border-t border-dashed border-amp-line pt-2 text-[10px] leading-snug text-amp-ink-muted dark:border-amp-dark-line dark:text-amp-dark-ink-muted">
            Landlord / operator view is for strategy only; lease abstracts control legal landlord and notice party.
          </div>
        </aside>

        {/* Main: map (fixed height) over a full-width store-card grid */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="relative z-0 h-[520px] flex-shrink-0 overflow-hidden rounded-lg border border-amp-line dark:border-amp-dark-line">
            {storesError && !hasStores ? (
              <div className="p-6">
                <NoDataCard />
              </div>
            ) : (
              <MapContainer
                center={[39.0, -96.5]}
                zoom={4}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom
                worldCopyJump={false}
                maxBoundsViscosity={1.0}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                  noWrap
                />
                <LockToUs />
                {filteredStores
                  .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng))
                  .map((store) => {
                    const pilot = isPilot(store);
                    const radius = markerRadius(store.sales?.ytd_ty, maxYtd);
                    const fill = landlordColor(store.landlord_group);
                    const ring = severityColor(store.severity);
                    return (
                      <Fragment key={store.store_no}>
                        {pilot && (
                          <CircleMarker
                            center={[store.lat, store.lng]}
                            radius={radius + 5}
                            pathOptions={{ color: PILOT_GOLD, weight: 3, opacity: 0.95, fillOpacity: 0 }}
                            interactive={false}
                          />
                        )}
                        <CircleMarker
                          center={[store.lat, store.lng]}
                          radius={radius}
                          pathOptions={{ color: ring, weight: 3.5, opacity: 1, fillColor: fill, fillOpacity: 0.88 }}
                          eventHandlers={{ click: () => openStore(store) }}
                        >
                          <Tooltip sticky>
                            {store.store_name}
                            {pilot && store.pilot_rank != null ? ` ★ Pilot #${store.pilot_rank}` : ''}
                            {' '}&middot; YTD {formatComp(store.sales?.ytd_comp)} &middot; {dash(store.landlord_group)}
                          </Tooltip>
                        </CircleMarker>
                      </Fragment>
                    );
                  })}
              </MapContainer>
            )}
          </div>

          {/* Store-card grid wrapped in the shared DataGridShell. The sidebar
              controls both map and grid so it cannot move into the toolbar slot;
              the shell provides the count line and internal scroll. */}
          <DataGridShell
            total={stores.length}
            loaded={filteredStores.length}
            countNoun="stores"
          >
            {isLoading && !hasStores ? (
              <p className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
            ) : !hasStores ? (
              <div className="p-3"><NoDataCard /></div>
            ) : (
              <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredStores.map((s) => (
                  <StoreCard key={s.store_no} store={s} onSelect={openStore} />
                ))}
              </div>
            )}
          </DataGridShell>
        </div>
      </div>

      {selectedStore && (
        <StoreDetailModal store={selectedStore} onClose={() => setSelectedStore(null)} />
      )}
    </div>
  );
}

export function RetailFleetMap() {
  return (
    <ErrorBoundary>
      <RetailFleetMapInner />
    </ErrorBoundary>
  );
}
